/**
 * The Director — the layer that turns the engine's event stream into time.
 *
 * ============================== WHAT THIS IS ===================================
 * The engine is synchronous and instant: `Game.startHand()` and `Game.act()` each run
 * every bot through to the next point the human is needed and return. That is right
 * for the engine — it is what makes a seeded session replay byte-identically — and it
 * is wrong for a table, where three opponents acting in the same millisecond is not a
 * poker game. So above the engine sits this: a driver that owns the hand loop, drains
 * the events after every step, and plays them back on a TIMELINE at the table's pace,
 * with a think time before every bot action and a clock on the human.
 *
 * Three rules, all of them load-bearing:
 *
 *   1. The engine is untouched. This file calls `startHand`, `stepOnce`, `act`,
 *      `drainEvents`, `tableState`, `heroOptions`, and reads `stacks`, `hands`, `phase`
 *      and `sessionCommitment`. It never reaches into a `Hand`, and it never draws from
 *      the engine's RNG — its own randomness (how long a bot appears to think) comes
 *      from a driver-side generator seeded from the session commitment, so a replayed
 *      session LOOKS the same too, and a stepped session is the same session as a flat
 *      one. `director.test.ts` proves the second half against `Game.run()`.
 *   2. Nothing here touches the DOM. Timers go through a `Clock` so the tests can run a
 *      whole hand in no time at all, and what the scene needs — the presented table,
 *      the clocks, whose cards have arrived — comes out as a plain snapshot through
 *      `onChange`. Flights are announced through `onStep` and drawn elsewhere.
 *   3. The scene renders from the PRESENTED state, never the engine's. See `display.ts`
 *      for why there are two and how they are kept equal.
 *
 * ## The loop
 *
 * `dealNext()` calls `startHand()`, drains, then loops `stepOnce()` draining after each
 * step until the engine says `awaiting` or `hand_over`. Today `startHand` has already
 * run the bots by the time it returns, so the loop finds nothing left to step; it is
 * written this way so that an engine which one day stops running the bots inside
 * `startHand` needs no change here. Every drained event becomes one or more steps on
 * the timeline. The timeline plays; when it reaches the `await` step the human clock
 * starts and the action bar goes live; `act()` cancels the clock, applies the action,
 * and the loop goes round again.
 * ===============================================================================
 */
import type { Game } from "@engine/game";
import type { TableEvent } from "@engine/events";
import type { TableState } from "@engine/table-state";
import type { Action, Street } from "@engine/types";
import { timeoutAction } from "@engine/controller";
import {
  applyEvent, handStartState, movesChips, refundsAt, type SceneEvent,
} from "./display";
import { HUMAN_CLOCK, thinkTime, type Durations } from "./pace";

/* ------------------------------------------------------------------ clock --- */

/** Wall-clock and timers, injectable so a test can run a hand instantly. */
export interface Clock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const realClock: Clock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

/* ------------------------------------------------------------- driver RNG --- */

/**
 * mulberry32 — small, fast, and NOT the engine's ChaCha stream.
 *
 * It only decides how long a bot looks like it is thinking. It must be its own
 * generator because the transport test's whole promise is that the pacing layer draws
 * nothing from `handRng`; and it is seeded from the session commitment so that the same
 * session watched twice has the same rhythm.
 */
export function driverRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A 32-bit hash of a string, for seeding the driver RNG from the commitment. */
export function seedFromString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h;
}

/* ------------------------------------------------------------------ steps --- */

/**
 * One timed thing on the timeline.
 *
 * `dur` is how long before the NEXT step starts; `land` is how long before this step's
 * state change is applied. Usually equal. A deal has `land` longer than `dur` — the
 * next card leaves before this one arrives — and a shuffle lands at once so the old
 * hand is mucked the moment the riffle starts.
 */
export type Step =
  | { kind: "shuffle"; dur: number; land: number; event: Extract<TableEvent, { t: "hand_start" }> }
  | { kind: "button"; dur: number; land: number; event: Extract<TableEvent, { t: "button_move" }> }
  | { kind: "post"; dur: number; land: number; seat: number; amount: number; event: Extract<TableEvent, { t: "post" }> }
  | { kind: "deal"; dur: number; land: number; seat: number; index: number; event: Extract<TableEvent, { t: "deal_hole" }> }
  | { kind: "hole"; dur: number; land: number; event: Extract<TableEvent, { t: "hole_shown" }> }
  /** A bot's clock. `to_act` lands at the start; the action follows as its own step. */
  | { kind: "think"; dur: number; land: number; seat: number; event: { t: "to_act"; seat: number } }
  | { kind: "action"; dur: number; land: number; seat: number; action: Action; chips: boolean; event: Extract<TableEvent, { t: "action" }> }
  /** Every live bet slides into the pot. Its own step so the flips wait for it. */
  | { kind: "collect"; dur: number; land: number; seats: number[]; event: { t: "collect" } }
  | { kind: "flip"; dur: number; land: number; event: Extract<TableEvent, { t: "board" }> }
  | { kind: "reveal"; dur: number; land: number; seat: number; event: Extract<TableEvent, { t: "showdown" }> }
  | { kind: "push"; dur: number; land: number; seat: number; amount: number; event: Extract<TableEvent, { t: "pot_push" }> }
  | { kind: "refund"; dur: number; land: number; seat: number; amount: number; event: { t: "refund"; seat: number; amount: number } }
  | { kind: "end"; dur: number; land: number; event: Extract<TableEvent, { t: "hand_end" }> }
  /** The human is to act. The timeline stops here until `act()`. */
  | { kind: "await"; dur: number; land: number; seat: number; event: { t: "to_act"; seat: number } }
  /** A held breath: nothing moves. The run-out and the showdown are made of these. */
  | { kind: "pause"; dur: number; land: number }
  /** The hand is over and presented. Auto-deal counts from here. */
  | { kind: "hand_over"; dur: number; land: number };

export type StepKind = Step["kind"];

/* --------------------------------------------------------------- snapshot --- */

/** A seat's clock, as the ring draws it. A superset of Phase 2's `SeatTimer`. */
export interface SeatClock {
  /** Where the ring is heading: 1 full, 0 empty. */
  t: number;
  /** Where it starts. The ring transitions from `from` to `t` over `ms`. */
  from?: number;
  ms?: number;
  hurry?: boolean;
}

export type DirectorPhase = "idle" | "playing" | "awaiting" | "between" | "finished";

/** Everything the scene draws. Plain data, replaced whole on every change. */
export interface DirectorSnapshot {
  /** The table as presented so far. Null before the first hand. */
  state: TableState | null;
  phase: DirectorPhase;
  clocks: Record<number, SeatClock>;
  /** A bot is thinking. `secondsLeft` is what the band prints. */
  waiting: { seat: number; secondsLeft: number } | null;
  /** The hero's clock, once it is their turn. */
  heroClock: { hurry: boolean; secondsLeft: number } | null;
  /** Cards that have ARRIVED at each seat this hand, so a peek appears as it is dealt. */
  dealt: Record<number, number>;
  lastAction: { seat: number; action: Action } | null;
}

export interface DirectorOptions {
  game: Game;
  /** Which seat is the human. -1 for a bot-only table (the harness plays one). */
  viewer: number;
  /** The durations at the current pace. Read on every step, so a change takes at once. */
  durations: () => Durations;
  onChange: (snap: DirectorSnapshot) => void;
  /** A step is starting: the flight layer draws it. Called before its state lands. */
  onStep?: (step: Step, before: TableState) => void;
  /** One tick per second of the hurry-up. The sound is Phase 6; this is its hook. */
  onTick?: (secondsLeft: number) => void;
  /** Deal the next hand on its own after `betweenHands`. */
  autoDeal?: boolean;
  /** Turbo tables and tests override the human clock; drills pass null for no clock. */
  humanClock?: { quiet: number; hurry: number } | null;
  clock?: Clock;
  /** Overrides the commitment-derived seed for the driver RNG. */
  seed?: number;
}

/* --------------------------------------------------------------- suspense --- */

/**
 * The batch, in the order a table would show it.
 *
 * The engine emits `pot_push` before `showdown`, because that is the order it settles
 * in; a table turns the hands over first and then pushes the pot, so the showdown
 * events are moved ahead of the pushes. And when everyone is all in, the engine runs
 * the board out in one `board` event and only then shows the hands — at a real table
 * the hands go on their backs BEFORE the run-out, which is the whole of the suspense.
 * So when the last `board` event is followed by no more actions and there is a
 * showdown, the showdowns move ahead of that board as well, and the board is returned
 * as `runout` so the flips can be paced.
 *
 * Nothing here changes what happened, only when it is shown; the presented table
 * still ends exactly where the engine's does (the tests check).
 */
export function arrangeForSuspense(events: TableEvent[]): {
  events: TableEvent[];
  /** The run-out `board` event, if this batch has one; its reveals ride with it. */
  runout: Extract<TableEvent, { t: "board" }> | null;
  reveals: Extract<TableEvent, { t: "showdown" }>[];
} {
  const showdowns = events.filter((e): e is Extract<TableEvent, { t: "showdown" }> => e.t === "showdown");
  if (showdowns.length === 0) return { events, runout: null, reveals: [] };
  const rest = events.filter((e) => e.t !== "showdown");
  let lastBoard = -1;
  for (let i = 0; i < rest.length; i++) if (rest[i]!.t === "board") lastBoard = i;
  const actionAfter = lastBoard >= 0 && rest.slice(lastBoard + 1).some((e) => e.t === "action");
  if (lastBoard >= 0 && !actionAfter) {
    /* A run-out: the hands are shown between the collect and the first card. */
    return { events: rest, runout: rest[lastBoard] as Extract<TableEvent, { t: "board" }>, reveals: showdowns };
  }
  let at = rest.findIndex((e) => e.t === "pot_push");
  if (at < 0) at = rest.findIndex((e) => e.t === "hand_end");
  if (at < 0) at = rest.length;
  return { events: [...rest.slice(0, at), ...showdowns, ...rest.slice(at)], runout: null, reveals: [] };
}

/* --------------------------------------------------------------- director --- */

export class Director {
  private readonly game: Game;
  private readonly viewer: number;
  private readonly durations: () => Durations;
  private readonly clock: Clock;
  private readonly rng: () => number;
  private readonly onChange: (snap: DirectorSnapshot) => void;
  private readonly onStep?: (step: Step, before: TableState) => void;
  private readonly onTick?: (secondsLeft: number) => void;
  private readonly humanClock: { quiet: number; hurry: number } | null;
  autoDeal: boolean;

  /** The presented table, and the same table at the END of everything queued. */
  private presented: TableState | null = null;
  private shadow: TableState | null = null;
  private stacksAtStart: number[] = [];
  private dealt: Record<number, number> = {};
  private clocks: Record<number, SeatClock> = {};
  private waiting: DirectorSnapshot["waiting"] = null;
  private heroClock: DirectorSnapshot["heroClock"] = null;
  private lastAction: DirectorSnapshot["lastAction"] = null;
  private phase: DirectorPhase = "idle";

  private queue: Step[] = [];
  private running = false;
  private paused = false;
  private destroyed = false;
  /** The hero's decision is wanted and the clock is running (or would be). */
  private awaiting = false;
  private readonly timers = new Set<unknown>();
  /** The human clock's own timers, cancelled the moment the hero acts. */
  private readonly clockTimers = new Set<unknown>();
  private stepsSeen = 0;

  constructor(opts: DirectorOptions) {
    this.game = opts.game;
    this.viewer = opts.viewer;
    this.durations = opts.durations;
    this.clock = opts.clock ?? realClock;
    this.onChange = opts.onChange;
    this.onStep = opts.onStep;
    this.onTick = opts.onTick;
    this.autoDeal = opts.autoDeal ?? true;
    this.humanClock = opts.humanClock === undefined
      ? { quiet: HUMAN_CLOCK.quiet, hurry: HUMAN_CLOCK.hurry }
      : opts.humanClock;
    this.rng = driverRng(opts.seed ?? seedFromString(opts.game.sessionCommitment));
  }

  /* ------------------------------------------------------------ public --- */

  snapshot(): DirectorSnapshot {
    return {
      state: this.presented, phase: this.phase, clocks: { ...this.clocks },
      waiting: this.waiting, heroClock: this.heroClock, dealt: { ...this.dealt },
      lastAction: this.lastAction,
    };
  }

  /** How many steps have started. The harness uses it to know the table is moving. */
  get stepCount(): number { return this.stepsSeen; }
  get isAwaiting(): boolean { return this.awaiting; }
  get isIdle(): boolean { return !this.running && !this.awaiting && this.queue.length === 0; }

  /**
   * Deal the next hand.
   *
   * Refuses quietly when there is nothing to deal — the auto-deal timer and a "Next
   * hand" button can both fire for the same hand — and when the timeline is still
   * presenting the last one, because a deal that starts while the pot is still flying
   * is the reflow this whole layer exists to prevent.
   */
  dealNext(): boolean {
    if (this.destroyed) return false;
    const g = this.game;
    if (g.phase !== "hand_over" && g.phase !== "idle") return false;
    if (this.running || this.awaiting || this.queue.length > 0) return false;
    g.startHand();
    this.ingest(g.drainEvents());
    this.pump();
    return true;
  }

  /**
   * The hero's action, from the action bar or from the clock running out.
   *
   * Applied to the engine at once — the engine's truth does not wait for a chip to
   * slide — and then presented at pace like everything else.
   */
  act(action: Action): boolean {
    if (this.destroyed || !this.awaiting) return false;
    this.stopHumanClock();
    try {
      this.game.act(action);
    } catch (err) {
      /*
       * The engine refused it. The bar only offers legal actions and the clock uses
       * `timeoutAction`, so this is a bug somewhere — but the hand must not be left
       * stranded with no clock and no way to act: the decision stays open, the clock
       * restarts, and the error goes up to whoever called.
       */
      this.startHumanClock();
      throw err;
    }
    this.awaiting = false;
    this.heroClock = null;
    delete this.clocks[this.viewer];
    this.phase = "playing";
    this.ingest(this.game.drainEvents());
    this.pump();
    return true;
  }

  /** Stop between steps. The step in flight finishes; the next does not start. */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    /* A paused clock is a stopped clock: the human keeps whatever time was left. */
    this.stopHumanClock();
  }

  resume(): void {
    if (!this.paused || this.destroyed) return;
    this.paused = false;
    if (this.awaiting) this.startHumanClock();
    else if (!this.running) this.next();
  }

  /** Leaving the table. Every timer dies here; nothing fires after this returns. */
  destroy(): void {
    this.destroyed = true;
    this.stopHumanClock();
    for (const h of this.timers) this.clock.clearTimeout(h);
    this.timers.clear();
    this.queue = [];
    this.running = false;
  }

  /* --------------------------------------------------------------- loop --- */

  private pump(): void {
    const g = this.game;
    let guard = 0;
    for (;;) {
      if (++guard > 500) throw new Error("the hand did not terminate");
      const r = g.stepOnce();
      this.ingest(g.drainEvents());
      if (r.kind === "acted") continue;
      if (r.kind === "awaiting") {
        this.push({ kind: "await", dur: 0, land: 0, seat: r.seat, event: { t: "to_act", seat: r.seat } });
      } else {
        this.push({ kind: "hand_over", dur: 0, land: 0 });
      }
      break;
    }
    if (!this.running && !this.paused) this.next();
  }

  /**
   * Events become steps, in order, with the beats the events do not carry: a think
   * before every bot action, a collect before the flop, a refund before the end.
   * `shadow` is the table as it will be when each step plays, so a think time can be
   * chosen for the spot the bot will actually be in.
   */
  private ingest(raw: TableEvent[]): void {
    const d = this.durations();
    const { events, runout, reveals } = arrangeForSuspense(raw);
    for (const e of events) {
      switch (e.t) {
        case "hand_start": {
          const engine = this.game.tableState(this.viewer);
          if (!engine) break;
          /*
           * Where the stacks were before this hand. `game.stacks` is written back only
           * when a hand finishes, so during a hand it still holds the starting stacks;
           * if the hand finished inside the same `startHand` call — a walk, or a
           * short-stacked hero all in from the blind — the hand's record has them.
           */
          const rec = this.game.hands[this.game.hands.length - 1];
          const finished = this.game.phase === "hand_over" || this.game.phase === "finished";
          this.stacksAtStart = finished && rec && rec.index === e.hand
            ? [...rec.stacksBefore] : [...this.game.stacks];
          this.shadow = handStartState(engine, this.stacksAtStart, e);
          this.push({ kind: "shuffle", dur: d.shuffle, land: 0, event: e });
          break;
        }
        case "button_move":
          /* Lands at once: the disc's own CSS transition is the hop, over `--hop-ms`. */
          this.push({ kind: "button", dur: d.buttonHop, land: 0, event: e });
          break;
        case "post":
          this.push({ kind: "post", dur: d.betSlide, land: d.betSlide, seat: e.seat, amount: e.amount, event: e });
          break;
        case "deal_hole":
          this.push({ kind: "deal", dur: d.dealStagger, land: d.deal, seat: e.seat, index: e.index, event: e });
          break;
        case "hole_shown":
          this.settleDeal(d);
          this.push({ kind: "hole", dur: d.flip, land: 0, event: e });
          break;
        case "action": {
          this.settleDeal(d);
          const s = this.shadow!;
          if (e.seat !== this.viewer) {
            const facingAllIn = s.seats.some((x) => x.allIn && !x.folded && x.index !== e.seat && x.committed > 0);
            const ms = thinkTime(this.rng(), {
              street: s.street as Street, potBb: s.pot / (s.rules.bigBlind || 1), facingAllIn,
            }, d.pace);
            this.push({ kind: "think", dur: ms, land: 0, seat: e.seat, event: { t: "to_act", seat: e.seat } });
          }
          const chips = movesChips(e.action);
          const fold = e.action.kind === "fold";
          const dur = chips ? d.betSlide : fold ? d.muck : d.actionBeat;
          /*
           * A fold lands at once: the seat's cards leave the felt as their copies slide
           * to the muck, so a card is never in two places. Chips land when they arrive.
           */
          this.push({ kind: "action", dur, land: fold ? 0 : dur, seat: e.seat, action: e.action, chips, event: e });
          break;
        }
        case "burn":
          break;
        case "board": {
          const live = this.shadow!.seats.filter((x) => x.committed > 0).map((x) => x.index);
          if (live.length > 0) {
            /*
             * Lands at once: the bets vanish from the felt as their copies take off,
             * so a chip is never in two places. The pot number does not move here —
             * the engine's pot already counted every bet — so nothing waits on it.
             */
            this.push({ kind: "collect", dur: d.potCollect, land: 0, seats: live, event: { t: "collect" } });
          }
          /*
           * One flip per card, staggered; a run-out is several streets in one event.
           * On a run-out — the chips are all in, the hands are already face up — each
           * street waits: a held breath before the flop and the turn, a longer one
           * before the river. `already` is how many board cards were out before this.
           */
          const already = this.shadow!.board.length;
          if (e === runout) {
            for (const r of reveals) this.push({ kind: "reveal", dur: d.flip, land: 0, seat: r.seat, event: r });
          }
          e.cards.forEach((c, i) => {
            const k = already + i;
            if (e === runout && (k === 0 || k === 3 || k === 4)) {
              this.push({ kind: "pause", dur: k === 4 ? d.riverPause : d.runoutPause, land: 0 });
            }
            const last = i === e.cards.length - 1;
            this.push({
              kind: "flip", dur: last ? d.flip : d.flipStagger, land: 0,
              event: { t: "board", street: e.street, cards: [c] },
            });
          });
          break;
        }
        case "showdown":
          this.push({ kind: "reveal", dur: d.flip, land: 0, seat: e.seat, event: e });
          break;
        case "pot_push": {
          /* The first push waits a beat after the hands were shown (or the river fell). */
          const prev = this.queue[this.queue.length - 1];
          if (prev && (prev.kind === "reveal" || prev.kind === "flip")) {
            this.push({ kind: "pause", dur: d.showdownPause, land: 0 });
          }
          const live = this.shadow!.seats.filter((x) => x.committed > 0).map((x) => x.index);
          if (live.length > 0) {
            this.push({ kind: "collect", dur: d.potCollect, land: 0, seats: live, event: { t: "collect" } });
          }
          this.push({ kind: "push", dur: d.potPush, land: d.potPush, seat: e.seat, amount: e.amount, event: e });
          break;
        }
        case "hand_end": {
          for (const r of refundsAt(this.shadow!, this.stacksAtStart, e.net)) {
            this.push({
              kind: "refund", dur: d.potPush, land: d.potPush, seat: r.seat, amount: r.amount,
              event: { t: "refund", seat: r.seat, amount: r.amount },
            });
          }
          this.push({ kind: "end", dur: 0, land: 0, event: e });
          break;
        }
      }
    }
  }

  /** The last card of a deal waits to land before anything else happens. */
  private settleDeal(d: Durations): void {
    const last = this.queue[this.queue.length - 1];
    if (last && last.kind === "deal" && last.dur < d.deal) last.dur = d.deal;
  }

  private push(step: Step): void {
    this.queue.push(step);
    if ("event" in step && this.shadow) this.shadow = this.applyScene(this.shadow, step.event);
  }

  private applyScene(state: TableState, e: SceneEvent): TableState {
    return applyEvent(state, e, this.stacksAtStart);
  }

  /* ----------------------------------------------------------- playback --- */

  private schedule(fn: () => void, ms: number, bag: Set<unknown> = this.timers): void {
    const h = this.clock.setTimeout(() => { bag.delete(h); if (!this.destroyed) fn(); }, ms);
    bag.add(h);
  }

  private next(): void {
    if (this.destroyed || this.paused) return;
    const step = this.queue.shift();
    if (!step) { this.running = false; return; }
    this.running = true;
    this.stepsSeen++;
    this.begin(step);
    if (step.kind === "await" || step.kind === "hand_over") { this.running = false; return; }
    if (step.land > 0) this.schedule(() => this.land(step), step.land);
    this.schedule(() => this.next(), step.dur);
  }

  /** A step starts: announce it for the flight layer, then move the clocks. */
  private begin(step: Step): void {
    const before = this.presented;
    if (step.kind === "shuffle") {
      const engine = this.game.tableState(this.viewer);
      this.presented = handStartState(engine ?? this.presented!, this.stacksAtStart, step.event);
      this.dealt = {};
      this.lastAction = null;
      this.phase = "playing";
      this.onStep?.(step, before ?? this.presented);
      this.emit();
      return;
    }
    if (before) this.onStep?.(step, before);

    switch (step.kind) {
      case "think":
        this.presented = this.applyScene(this.presented!, step.event);
        this.clocks = { [step.seat]: { t: 0, from: 1, ms: step.dur } };
        this.waiting = { seat: step.seat, secondsLeft: Math.ceil(step.dur / 1000) };
        this.countdown(step.dur, (left) => { this.waiting = { seat: step.seat, secondsLeft: left }; this.emit(); });
        break;
      case "action":
        this.clocks = {};
        this.waiting = null;
        /* A fold lands now; a bet lands when the chip arrives. */
        if (step.land === 0) { this.land(step); return; }
        break;
      case "await":
        this.presented = this.applyScene(this.presented!, step.event);
        this.awaiting = true;
        this.phase = "awaiting";
        this.startHumanClock();
        break;
      case "hand_over":
        this.phase = this.game.phase === "finished" ? "finished" : "between";
        this.clocks = {};
        this.waiting = null;
        if (this.autoDeal && this.phase === "between") {
          this.schedule(() => { this.dealNext(); }, this.durations().betweenHands);
        }
        break;
      default:
        if (step.land === 0) { this.land(step); return; }
    }
    this.emit();
  }

  /** A step's state change applies. */
  private land(step: Step): void {
    if (!this.presented) return;
    switch (step.kind) {
      case "deal":
        this.dealt[step.seat] = (this.dealt[step.seat] ?? 0) + 1;
        break;
      case "action":
        this.lastAction = { seat: step.seat, action: step.action };
        break;
      default:
        break;
    }
    if ("event" in step) this.presented = this.applyScene(this.presented, step.event);
    this.emit();
  }

  /** A one-second cadence for a numeral, ending at zero. */
  private countdown(ms: number, each: (secondsLeft: number) => void, bag: Set<unknown> = this.timers): void {
    const whole = Math.ceil(ms / 1000);
    for (let s = 1; s < whole; s++) {
      const left = whole - s;
      this.schedule(() => each(left), ms - left * 1000, bag);
    }
  }

  /* -------------------------------------------------------- human clock --- */

  private startHumanClock(): void {
    const hc = this.humanClock;
    if (!hc) { this.heroClock = null; this.emit(); return; }
    this.clocks = { [this.viewer]: { t: 0, from: 1, ms: hc.quiet } };
    this.heroClock = { hurry: false, secondsLeft: Math.ceil((hc.quiet + hc.hurry) / 1000) };
    this.emit();
    this.countdown(hc.quiet, (left) => {
      this.heroClock = { hurry: false, secondsLeft: left + Math.ceil(hc.hurry / 1000) };
      this.emit();
    }, this.clockTimers);
    this.schedule(() => {
      this.clocks = { [this.viewer]: { t: 0, from: 1, ms: hc.hurry, hurry: true } };
      const secs = Math.ceil(hc.hurry / 1000);
      this.heroClock = { hurry: true, secondsLeft: secs };
      this.onTick?.(secs);
      this.emit();
      this.countdown(hc.hurry, (left) => {
        this.heroClock = { hurry: true, secondsLeft: left };
        this.onTick?.(left);
        this.emit();
      }, this.clockTimers);
      this.schedule(() => this.expire(), hc.hurry, this.clockTimers);
    }, hc.quiet, this.clockTimers);
  }

  private stopHumanClock(): void {
    for (const h of this.clockTimers) this.clock.clearTimeout(h);
    this.clockTimers.clear();
  }

  /** Out of time: check if you can, otherwise fold — through the same path as a click. */
  private expire(): void {
    const options = this.game.heroOptions();
    if (options.length === 0) return;
    this.act(timeoutAction(options));
  }

  private emit(): void {
    if (this.destroyed) return;
    this.onChange(this.snapshot());
  }
}
